import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCards } from '../../hooks/useCards';
import { supabase } from '../../lib/supabase';
import { useDecks } from '../../hooks/useDecks';
import { Breadcrumbs } from '../../components/navigation/Breadcrumbs';
import { DeckFormHeader } from '../../components/decks/DeckForm';
import { DeckCardSelector } from '../../components/decks/DeckCardSelector';
import { DeckPreviewPanel } from '../../components/decks/DeckPreviewPanel';
import { PokemonCard, FilterState } from '../../types/pokemon';
import { PageTitle } from '../../components/layout/PageTitle';
import { useI18n } from '../../contexts/I18nContext';

export function CreateDeck() {
  const navigate = useNavigate();
  const { t, lang, getPathForLang } = useI18n();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [selectedCards, setSelectedCards] = useState<Map<number, number>>(new Map());
  const [filters, setFilters] = useState<FilterState>({
    pack: null,
    rarity: null,
    set: null,
    search: '',
    type: null,
    energy: null,
    showMissing: false
  });
  const [selectedCardsData, setSelectedCardsData] = useState<PokemonCard[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const { createDeck } = useDecks();
  
  const {
    cards,
    loading: cardsLoading,
    filterOptions,
    hasMore,
    isLoadingMore,
    loadMore,
    totalCount
  } = useCards(filters);

  const totalCards = Array.from(selectedCards.values()).reduce((sum, quantity) => sum + quantity, 0);

  React.useEffect(() => {
    const fetchSelectedCards = async () => {
      const cardIds = Array.from(selectedCards.keys());
      if (cardIds.length === 0) {
        setSelectedCardsData([]);
        return;
      }

      const { data } = await supabase
        .from('cards')
        .select('*')
        .in('id', cardIds);

      setSelectedCardsData(data || []);
    };

    fetchSelectedCards();
  }, [selectedCards]);

  const handleCardSelect = (cardId: number, quantity: number) => {
    const newSelectedCards = new Map(selectedCards);
    if (quantity === 0) {
      newSelectedCards.delete(cardId);
    } else {
      const currentTotal = Array.from(newSelectedCards.entries()).reduce((sum, [id, qty]) => {
        return sum + (id === cardId ? 0 : qty);
      }, 0);
      
      if (currentTotal + quantity <= 20) {
        newSelectedCards.set(cardId, quantity);
      }
    }
    setSelectedCards(newSelectedCards);
  };

  const handleRemoveCard = (cardId: number) => {
    const newSelectedCards = new Map(selectedCards);
    newSelectedCards.delete(cardId);
    setSelectedCards(newSelectedCards);
  };

  const validateDeck = () => {
    if (!name.trim()) {
      setError(t('create_deck.validation.name_required'));
      return false;
    }
    if (totalCards !== 20) {
      setError(t('create_deck.validation.twenty_cards'));
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setError(null);

    if (!validateDeck()) return;

    try {
      setIsSubmitting(true);
      const deck = await createDeck(name.trim(), description.trim(), isPublic);
      
      if (deck) {
        // Add cards to deck
        const cardsToAdd = Array.from(selectedCards.entries()).map(([cardId, quantity]) => ({
          deck_id: deck.id,
          card_id: cardId,
          quantity
        }));

        const { error: insertError } = await supabase
          .from('deck_cards')
          .insert(cardsToAdd);

        if (insertError) throw insertError;
        
        navigate(getPathForLang(`/decks/${deck.id}`));
      }
    } catch (err) {
      console.error('Error creating deck:', err);
      setError(err instanceof Error ? err.message : t('create_deck.error.generic'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const deckCards = selectedCardsData.map(card => ({
    card_id: card.id,
    quantity: selectedCards.get(card.id) || 0,
    card: {
      ...card,
      // S'assurer que le nom de la carte est affiché dans la bonne langue
      card_name: lang === 'en' ? card.card_en || card.card_fr : card.card_fr || card.card_en
    }
  }));

  return (
    <div className="container mx-auto px-4 py-8">
      <PageTitle title={t('create_deck.page_title')} />
      <Breadcrumbs
        items={[
          { label: t('decks_page.title'), path: getPathForLang('/decks') },
          { label: t('create_deck.new_deck'), path: getPathForLang('/decks/new') }
        ]}
      />

      <div className="mt-8">
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <DeckFormHeader
          name={name}
          onNameChange={setName}
          description={description}
          onDescriptionChange={setDescription}
          isPublic={isPublic}
          onVisibilityChange={setIsPublic}
          disabled={isSubmitting}
          title={t('create_deck.form_title')}
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <DeckCardSelector
              filters={filters}
              onFilterChange={setFilters}
              cards={cards}
              loading={cardsLoading}
              selectedCards={selectedCards}
              onUpdateQuantity={handleCardSelect}
              totalCards={totalCards}
              filterOptions={filterOptions}
              hasMore={hasMore}
              isLoadingMore={isLoadingMore}
              onLoadMore={loadMore}
              totalCount={totalCount}
            />
          </div>

          <div className="lg:col-span-1">
            <DeckPreviewPanel
              cards={deckCards}
              totalCards={totalCards}
              onSubmit={handleSubmit}
              submitLabel={t('create_deck.create_button')}
              isSubmitting={isSubmitting}
              onRemoveCard={handleRemoveCard}
            />
          </div>
        </div>
      </div>
    </div>
  );
}